import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useCallback, useEffect, useMemo, useRef, type JSX } from 'react';
import { useAtom } from 'jotai';
import type { UseFieldArrayReturn } from 'react-hook-form';
import { AcctState } from 'stores/atoms/acctState';
import { ChatState } from 'stores/atoms/chatState';
import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import type {
  Message,
  MessagePayload,
  MessageRole,
  WsChatSidebarConfig,
} from 'pages/ws/components/chatSidebar/WsChatSidebar';
import {
  clearContentFade,
  startContentFadeIn,
  startContentFadeOut,
} from 'pages/ws/components/chatSidebar/wsChatSidebar.effects';
import WsModalChatbotButton from 'pages/ws/components/modalChatbotButton/WsModalChatbotButton';
import { useWsCoverImageGenerate } from 'pages/ws/hooks/useWsCoverImageGenerate';
import {
  appendOneShotOption,
  COVER_IMAGE_LABEL,
  COVER_IMAGE_OFFER_MESSAGE,
  CREATE_COMMAND_LABEL,
  CREATE_COMMAND_PROMPT,
  isAiImageCountLimitReached,
  isContentNotGenerated,
  shouldOfferCoverImage,
  shouldOfferCreateCommand,
} from 'pages/ws/utils/wsContentAiChat';
import {
  buildAiChatCurrentContent,
  mapAiQuestionsToFormQuestions,
  type AiQuizQuestion,
} from 'pages/ws/quizEdit/utils/quizFormConverter';

/**
 * AI応答取得エラー時に表示する固定文言（3種別共通・動作仕様書 EV-content-chat-01 のエラー文言）。
 */
const AI_CHAT_ERROR_MESSAGE =
  '申し訳ございません。AIによる生成中にエラーが発生しました。しばらく時間をおいてから再度お試しください。';

interface WsQuizEditChatbotProps {
  getValues: () => QuizEditFormStateType;
  setValue: (
    name: keyof QuizEditFormStateType,
    value: QuizEditFormStateType[keyof QuizEditFormStateType]
  ) => void;
  questionFieldArray: UseFieldArrayReturn<QuizEditFormStateType, 'questions'>;
  resultFieldArray: UseFieldArrayReturn<QuizEditFormStateType, 'results'>;
  onCreateRef?: React.MutableRefObject<(() => void) | undefined>;
}

/**
 * クイズ編集画面用のチャットボットコンポーネント
 */
export default function WsQuizEditChatbot({
  getValues,
  setValue,
  questionFieldArray,
  resultFieldArray,
  onCreateRef,
}: WsQuizEditChatbotProps): JSX.Element {
  const [acctState, setAcctState] = useAtom(AcctState);
  const [chatState, setChatState] = useAtom(ChatState);
  const { postQuizAiQuestions } = useApiState();
  const { getActivePlan, isAiImageCountNoLimit } = useAppState();

  // 凍結 config 対策: chatConfig の closure は開いた時点で固定されるため、
  // 最新の acctState を ref 経由で参照する（WsChatSidebar.tsx の同パターンを踏襲）。
  const acctStateRef = useRef(acctState);
  // eslint-disable-next-line react-hooks/refs -- レンダーごとにrefを最新値に同期するパターン（イベントハンドラ用）
  acctStateRef.current = acctState;

  /**
   * answerPatch を現在のフォーム値へ適用する（results → questions の順）。
   * results は imageKey のみ保持（id/isNew を持たないため）、questions は questionImageKey /
   * answerImageKey を保持する。範囲判定は getValues() を基準にする（fieldArray.fields は
   * atom 凍結で stale になるため使用禁止）。
   */
  const applyAnswerPatch = useCallback(
    (patch: {
      title: string | null;
      description: string | null;
      questions: { index: number; question: AiQuizQuestion }[] | null;
      results:
        | {
            index: number;
            result: { scoreThreshold: number; title: string; descriptionHtml: string };
          }[]
        | null;
    }) => {
      if (patch.title !== null) setValue('title', patch.title);
      if (patch.description !== null) setValue('description', patch.description);

      // 本体（設問）が未生成のときは questions / results パッチを適用しない（プロンプト補足ルール 1 の構造的ガード）。
      // LLM 応答は確率的にブレるため画面側でも止める。
      if (isContentNotGenerated(getValues().questions)) {
        if ((patch.questions?.length ?? 0) > 0 || (patch.results?.length ?? 0) > 0) {
          console.warn(
            'コンテンツが未生成のため answerPatch の questions / results は適用せずスキップしました'
          );
        }
        return;
      }

      const currentResults = getValues().results ?? [];
      (patch.results ?? []).forEach(({ index, result }) => {
        if (index >= currentResults.length) {
          console.warn(`answerPatch.results の index ${index} が範囲外のためスキップしました`);
          return;
        }
        const existing = currentResults[index];
        resultFieldArray.update(index, {
          imageKey: existing.imageKey,
          scoreThreshold: result.scoreThreshold,
          title: result.title,
          descriptionHtml: result.descriptionHtml,
        });
      });

      const currentQuestions = getValues().questions ?? [];
      (patch.questions ?? []).forEach(({ index, question }) => {
        if (index >= currentQuestions.length) {
          console.warn(`answerPatch.questions の index ${index} が範囲外のためスキップしました`);
          return;
        }
        const existing = currentQuestions[index];
        const [mapped] = mapAiQuestionsToFormQuestions([question]);
        questionFieldArray.update(index, {
          ...existing,
          ...mapped,
          reportMailDescriptionHtml: question.descriptionHtml ?? '',
          questionImageKey: existing.questionImageKey ?? null,
          answerImageKey: existing.answerImageKey ?? null,
        });
      });
    },
    [getValues, questionFieldArray, resultFieldArray, setValue]
  );

  /**
   * AI 応答を画面へ適用する共通処理（onMessage / onSuggestionSelect から呼ばれる）。
   * applyAnswerContent が false（EV-content-chat-02: 案ボタンクリック後の概要要求）のときは
   * answerContent（全体生成）を反映せず console.warn で破棄する
   * （API は EV-01/02 を区別できず応答の形だけで課金するため、プロンプトで抑止するが確率的なブレは残る）。
   */
  const applyAiChatResponse = useCallback(
    (
      response: Awaited<ReturnType<typeof postQuizAiQuestions>>,
      setMessages: (value: React.SetStateAction<Message[]>) => void,
      opts: { applyAnswerContent: boolean }
    ) => {
      if (response.answerContent) {
        if (!opts.applyAnswerContent) {
          console.warn(
            '概要要求（EV-content-chat-02）に対して answerContent が返されたため反映せず破棄しました'
          );
        } else {
          const answerContent = response.answerContent;
          startContentFadeOut();

          setTimeout(() => {
            try {
              setValue('title', answerContent.title ?? undefined);
              setValue('description', answerContent.description ?? undefined);

              questionFieldArray.replace(mapAiQuestionsToFormQuestions(answerContent.questions));
              resultFieldArray.replace(answerContent.results);

              setTimeout(() => {
                startContentFadeIn();
              }, 100);

              // 表紙画像の提案（反映が成功した場合のみ提示する）。
              // ここに置く理由: ①title は反映後の値で判定する必要がある
              // ②反映が catch に落ちた場合は提案を出さないため
              const imageCountLimitReached = isAiImageCountLimitReached({
                aiImageUsageCount: acctStateRef.current.company.aiImageUsageCount,
                aiImageCountLimit: getActivePlan()?.aiImageCountLimit,
                noLimit: isAiImageCountNoLimit(),
              });
              if (shouldOfferCoverImage({ title: getValues().title, imageCountLimitReached })) {
                setMessages(prev => [
                  ...prev,
                  { role: 'ai' as MessageRole, text: COVER_IMAGE_OFFER_MESSAGE },
                ]);
                setMessages(prev =>
                  appendOneShotOption(prev, { type: 'coverImage' }, COVER_IMAGE_LABEL)
                );
              }
            } catch (e) {
              console.error('AIコンテンツ反映エラー:', e);
              clearContentFade();
              setMessages(prev => [
                ...prev,
                { role: 'ai' as MessageRole, text: AI_CHAT_ERROR_MESSAGE },
              ]);
            }
          }, 400);
        }
      } else if (response.answerPatch) {
        applyAnswerPatch(response.answerPatch);
      }

      setMessages(prev => [...prev, { role: 'ai' as MessageRole, text: response.answerText }]);

      if (response.suggestions && response.suggestions.length > 0) {
        setMessages(prev => [
          ...prev,
          ...response.suggestions!.map(suggestion => {
            const payload: MessagePayload = {
              type: 'suggestion',
              title: suggestion.title,
              description: suggestion.description,
            };
            return {
              role: 'option' as MessageRole,
              text: `【${suggestion.title}】\n\n${suggestion.description}`,
              payload,
            };
          }),
        ]);
      }

      // 「この内容で作成する」ボタンの提示（案クリック後の概要提示・通常の相談の両方で発生しうる）
      if (
        shouldOfferCreateCommand({
          response,
          title: getValues().title,
          description: getValues().description,
          questions: getValues().questions,
        })
      ) {
        setMessages(prev =>
          appendOneShotOption(
            prev,
            { type: 'command', prompt: CREATE_COMMAND_PROMPT },
            CREATE_COMMAND_LABEL
          )
        );
      }

      // AIテキスト生成数の更新（全レスポンスで実施）
      setAcctState(prev => ({
        ...prev,
        company: {
          ...prev.company,
          aiTextUsageCount: response?.aiTextUsageCount ?? acctState.company.aiTextUsageCount,
        },
      }));
    },
    [
      acctState.company,
      applyAnswerPatch,
      getActivePlan,
      getValues,
      isAiImageCountNoLimit,
      questionFieldArray,
      resultFieldArray,
      setAcctState,
      setValue,
    ]
  );

  // 表紙画像生成処理（EV-content-chat-11）。診断・クイズ・フォーム共通の hook に委譲する。
  const onCoverImageGenerate = useWsCoverImageGenerate({
    getTitle: () => getValues().title,
    getDescription: () => getValues().description,
    setImageKey: imageKey => setValue('imageKey', imageKey),
  });

  // チャット設定
  const chatConfig: WsChatSidebarConfig = useMemo(
    () => ({
      initialMessages: [
        '『誰向け』と『どんな目的』かを一言で伝えてくれたら、まずタイトルと説明の案を3つ提案するよ！',
        `例えば…

・『社員向けのコンプライアンス理解度クイズを作成して』
・『お客様向けの商品知識クイズを作成して』
・『学生向けの一般常識クイズを作成して』

といった感じだよ。気軽に書いてみてね！`,
      ],
      buttons: [],
      onSuggestionSelect: async (payload, setMessages, messages) => {
        setValue('title', payload.title);
        setValue('description', payload.description);

        // 未生成のときのみ概要要求 API を呼ぶ（生成済みなら反映メッセージのみで終了）
        if (!isContentNotGenerated(getValues().questions)) {
          setMessages(prev => [
            ...prev,
            { role: 'ai' as MessageRole, text: 'タイトルと説明を反映したよ！' },
          ]);
          return;
        }

        setMessages(prev => [
          ...prev,
          {
            role: 'ai' as MessageRole,
            text: 'タイトルと説明を反映したよ！この内容でどんなクイズを作るか整理するね',
          },
        ]);

        try {
          const currentContent = buildAiChatCurrentContent(getValues());
          const history = messages
            .filter(m => m.role !== 'option')
            .map(m => ({ role: m.role, text: m.text }));

          const response = await postQuizAiQuestions({
            companyId: acctState.company.id,
            prompt: `この案で進めます（タイトル: ${payload.title}）。まだ何も作らないでください。この案で進めた場合の構成の概要だけを説明してください。`,
            currentContent,
            history,
          });

          applyAiChatResponse(response, setMessages, { applyAnswerContent: false });
        } catch (error) {
          console.error('AI概要取得エラー:', error);
          setMessages(prev => [
            ...prev,
            { role: 'ai' as MessageRole, text: AI_CHAT_ERROR_MESSAGE },
          ]);
        }
      },
      onMessage: async (message, setMessages, messages) => {
        try {
          const currentContent = buildAiChatCurrentContent(getValues());
          const history = messages
            .filter(m => m.role !== 'option')
            .map(m => ({ role: m.role, text: m.text }));

          const response = await postQuizAiQuestions({
            companyId: acctState.company.id,
            prompt: message,
            currentContent,
            history,
          });

          applyAiChatResponse(response, setMessages, { applyAnswerContent: true });
        } catch (error) {
          console.error('AIクイズ生成エラー:', error);
          setMessages(prev => [
            ...prev,
            { role: 'ai' as MessageRole, text: AI_CHAT_ERROR_MESSAGE },
          ]);
        }
      },
      onCoverImageGenerate,
    }),
    [
      acctState.company,
      applyAiChatResponse,
      getValues,
      onCoverImageGenerate,
      postQuizAiQuestions,
      setValue,
    ]
  );

  // ページ離脱時にチャットの状態をリセット
  useEffect(() => {
    return () => {
      setChatState(prev => ({
        ...prev,
        open: false,
        messages: [],
        config: null,
      }));
    };
  }, [setChatState]);

  // チャットが開かれた時にchatConfigを設定
  useEffect(() => {
    if (chatState.open && !chatState.config) {
      setChatState(prev => ({
        ...prev,
        config: chatConfig,
      }));
    }
  }, [chatState.open, chatState.config, setChatState, chatConfig]);

  // AIによるクイズ作成を行う（新規作成時はメッセージをリセット）
  const onCreateQuiz = useCallback(() => {
    setChatState(prev => ({
      ...prev,
      open: true,
      config: chatConfig,
      messages: [], // 新規作成時はメッセージをリセット
    }));
  }, [chatConfig, setChatState]);

  // 親コンポーネントから onCreateQuiz を呼び出せるようにする
  useEffect(() => {
    if (onCreateRef) {
      onCreateRef.current = onCreateQuiz;
    }
  }, [onCreateQuiz, onCreateRef]);

  // チャットを開く（既存のメッセージを保持）
  const openChat = useCallback(() => {
    setChatState(prev => ({
      ...prev,
      open: true,
      config: chatConfig,
      // 既存のメッセージを保持
    }));
  }, [chatConfig, setChatState]);

  return (
    <>
      {/* フローティングAIチャットボタン（AIチャットが開いているときは非表示） */}
      {!chatState.open && <WsModalChatbotButton onOpen={openChat} title="AIでクイズ作成" />}
    </>
  );
}
